import { useCallback, useEffect, useState } from "react";
import { api, fmtTime } from "@/api";
import { serviceLabel, sourceLabel, sourceStatusLabel, telegramLabel } from "@/i18n";

const StatusBadge = ({ ok, text, testId }) => (
  <span className={`status-badge ${ok ? "ok" : "err"}`} data-testid={testId}>{text}</span>
);

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setData((await api.get("/status")).data); }
    catch { setMessage("加载状态失败"); }
  }, []);
  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);
  const action = async (path) => {
    setBusy(true); setMessage("");
    try {
      const result = (await api.post(path)).data;
      if (result.detail === "preview_outbound_disabled") {
        setMessage(path === "/test-telegram"
          ? "Preview 已禁止向正式群发送测试消息"
          : "Preview 安全锁已启用，无法恢复后台监听");
      } else {
        setMessage(path === "/test-telegram"
          ? (result.ok ? "测试消息已发送" : "测试消息发送失败") : "状态已更新");
      }
    } catch { setMessage("操作失败"); }
    setBusy(false); load();
  };
  const running = data?.service === "Running";
  const components = data?.components || {};
  const componentRows = [
    ["消息抓取", components.signal_crawler?.status === "ok", components.signal_crawler?.status === "ok" ? "正常" : "异常"],
    ["自动交易", components.auto_trading_worker?.status === "ok", components.auto_trading_worker?.status === "ok" ? "正常" : "异常"],
    ["Telegram 推送", components.telegram_sender?.status === "ok", components.telegram_sender?.status === "ok" ? "正常" : "异常"],
    ["行情验证", components.market_verification_worker?.status === "ok", components.market_verification_worker?.status === "ok" ? "正常" : "异常"],
    ["余额监控", components.balance_monitor?.status === "ok", components.balance_monitor?.status === "ok" ? "正常" : "异常"],
  ];
  const executionState = (row, isLive = false) => {
    if (!row?.master_enabled || (isLive && row?.arming_state !== "ENABLED")) return { ok: true, text: "未启用" };
    if (!row?.connection_confirmed) return { ok: false, text: "连接异常" };
    if (row?.protection_failure_active) return { ok: false, text: "保护异常" };
    return { ok: true, text: "正常" };
  };
  const demoState = executionState(components.demo_execution);
  const liveState = executionState(components.live_execution, true);
  return (
    <section data-testid="status-page">
      <div className="page-heading"><div><p className="eyebrow">系统运行状态</p><h1>转发状态</h1></div></div>
      <div className="status-strip">
        <div><span>后台服务</span><StatusBadge ok={running} text={serviceLabel(data?.service)} testId="service-value" /></div>
        <div><span>Telegram 连接</span><StatusBadge ok={data?.telegram === "Connected"} text={telegramLabel(data?.telegram)} testId="telegram-value" /></div>
      </div>
      <div className="component-status-grid" data-testid="component-status-grid">
        {componentRows.map(([label, ok, text]) => <div className="component-status-card" key={label}>
          <span>{label}</span><StatusBadge ok={ok} text={text} />
        </div>)}
        <div className="component-status-card"><span>Demo 执行</span><StatusBadge ok={demoState.ok} text={demoState.text} /></div>
        <div className="component-status-card"><span>Live 执行</span><StatusBadge ok={liveState.ok} text={liveState.text} /></div>
      </div>
      <div className="table-frame wide"><table data-testid="sources-table"><thead><tr>
        <th data-testid="source-header-name">来源号机</th><th data-testid="source-header-status">状态</th><th data-testid="source-header-last-post">最新帖子 ID</th><th data-testid="source-header-last-check">最近检查</th><th data-testid="source-header-last-new">最近新帖</th>
        <th data-testid="source-header-last-success">最后成功抓取</th><th data-testid="source-header-last-discovered">最后发现消息</th><th data-testid="source-header-source-time">最近 Post 时间</th><th data-testid="source-header-http">HTTP</th><th data-testid="source-header-failures">连续失败</th><th data-testid="source-header-parser">最近 Parser 结果</th><th data-testid="source-header-alerts">已发送提醒</th><th data-testid="source-header-ignored">已忽略</th>
      </tr></thead><tbody>{(data?.sources || []).map((source) => {
        const key = source.key.replace(/_/g, "-");
        const sourceHealthy = ["ok", "ONLINE", "NO_NEW_POST"].includes(source.status);
        return <tr key={source.key} data-testid={`source-row-${key}`}>
          <td data-testid={`source-name-${key}`}>{sourceLabel(source.key, source.name)}</td>
          <td><StatusBadge ok={sourceHealthy} text={sourceStatusLabel(source.status)} testId={`source-status-${key}`} /></td>
          <td className="mono" data-testid={`source-last-post-${key}`}>{source.last_post_id ?? "—"}</td>
          <td className="mono" data-testid={`source-last-check-${key}`}>{fmtTime(source.last_check)}</td>
          <td className="mono" data-testid={`source-last-new-${key}`}>{fmtTime(source.last_new_post)}</td>
          <td className="mono" data-testid={`source-last-success-${key}`}>{fmtTime(source.last_successful_fetch_at)}</td>
          <td className="mono" data-testid={`source-last-discovered-${key}`}>{fmtTime(source.last_discovered_message_at)}</td>
          <td className="mono" data-testid={`source-latest-post-time-${key}`}>{fmtTime(source.latest_source_post_time)}</td>
          <td className="mono" data-testid={`source-http-status-${key}`}>{source.http_status ?? "—"}</td>
          <td className="mono" data-testid={`source-failure-count-${key}`}>{source.consecutive_failures ?? "—"}</td>
          <td data-testid={`source-parser-result-${key}`}>{source.last_parser_result || "—"}</td>
          <td className="mono" data-testid={`source-alerts-sent-${key}`}>{source.alerts_sent ?? "—"}</td>
          <td className="mono" data-testid={`source-ignored-${key}`}>{source.ignored ?? "—"}</td>
        </tr>;
      })}</tbody></table></div>
      <div className="counter-strip">
        <span>今日处理 <b data-testid="processed-today">{data?.processed_today ?? 0}</b></span>
        <span>今日发送 <b data-testid="sent-today">{data?.sent_today ?? 0}</b></span>
        <span>最后发送 <b data-testid="last-telegram-sent">{fmtTime(data?.last_telegram_sent)}</b></span>
      </div>
      <div className="actions">
        <button data-testid="test-telegram-btn" onClick={() => action("/test-telegram")} disabled={busy}>测试 Telegram</button>
        <button data-testid="toggle-worker-btn" className="secondary" onClick={() => action(`/worker/${running ? "pause" : "resume"}`)} disabled={busy}>{running ? "暂停监听" : "恢复监听"}</button>
      </div>
      {message && <p className="action-message" data-testid="action-message">{message}</p>}
    </section>
  );
}